// src/frontend_react/src/components/admin/UserForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Input from '../../components/ui/Input/Input';
import Checkbox from '../../components/ui/Checkbox/Checkbox';
import Alert from '../../components/ui/Alert/Alert';
import Spinner from '../../components/ui/Spinner/Spinner';
import Typography from '../../components/ui/Typography/Typography';
import Stack from '../../components/ui/Stack/Stack'; // Ensure HStack is correctly imported if Stack.js exports it
import Box from '../../components/ui/Box/Box';
import Card from '../../components/ui/Card/Card';

import { notify } from '../../services/notificationService';

// Import the SHARED Admin CSS module
import adminPageStyles from './styles.module.css';

// API service
import { createAdminUser, updateAdminUser, getAdminUserById, getAdminRoles } from '../../services/api';

function UserFormPage({ isEditMode = false }) {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    organization: '',
    roles: [],
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [allRoles, setAllRoles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [formTitle, setFormTitle] = useState('Create New User');

  const fetchRoles = useCallback(async () => {
    try {
      const rolesData = await getAdminRoles();
      if (Array.isArray(rolesData)) {
        setAllRoles(rolesData.sort((a, b) => (a.display_name || a.name).localeCompare(b.display_name || b.name)));
      } else {
        setError("Failed to load roles definition.");
        setAllRoles([]);
      }
    } catch (err) {
      setError(err.message || "Error fetching roles definition.");
      setAllRoles([]);
    }
  }, []);

  const fetchUserData = useCallback(async (id) => {
    setError(null);
    try {
      const userData = await getAdminUserById(id);
      setFormData({
        username: userData.username || '',
        email: userData.email || '',
        password: '',
        confirmPassword: '',
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        organization: userData.organization || '',
        roles: userData.roles || [],
        is_active: userData.is_active !== undefined ? userData.is_active : true,
      });
      setFormTitle(`Edit User: ${userData.username || `ID: ${id}`}`);
    } catch (err) {
      setError(err.message || `Failed to fetch data for user ID ${id}.`);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    setIsLoadingData(true);
    fetchRoles().finally(() => {
      if (isEditMode && userId) {
        fetchUserData(userId);
      } else {
        setFormTitle('Create New User');
        setFormData({
          username: '', email: '', password: '', confirmPassword: '',
          first_name: '', last_name: '', organization: '', roles: [], is_active: true,
        });
        setIsLoadingData(false);
      }
    });
  }, [isEditMode, userId, fetchRoles, fetchUserData]);

  const validateField = useCallback((name, value) => {
    let error = '';
    switch (name) {
      case 'username':
        if (!value) error = 'Username is required.';
        else if (value.length < 3) error = 'Username must be at least 3 characters.';
        break;
      case 'email':
        if (!value) error = 'Email is required.';
        else if (!/\S+@\S+\.\S+/.test(value)) error = 'Please enter a valid email address.';
        break;
      case 'password':
        if (!isEditMode && !value) error = 'Password is required for new users.';
        else if (value && value.length < 8) error = 'Password must be at least 8 characters.';
        break;
      case 'confirmPassword':
        if (formData.password && value !== formData.password) {
          error = 'Passwords do not match.';
        }
        break;
      default:
        break;
    }
    setFormErrors(prev => ({ ...prev, [name]: error }));
    return error === ''; // Return true if valid, false if error
  }, [formData.password, isEditMode]);

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleRoleChange = (e) => {
    const { value: roleName, checked } = e.target;
    setFormData(prev => {
      const currentRoles = prev.roles;
      if (checked) {
        return { ...prev, roles: [...new Set([...currentRoles, roleName])] };
      } else {
        return { ...prev, roles: currentRoles.filter(name => name !== roleName) };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isUsernameValid = validateField('username', formData.username);
    const isEmailValid = validateField('email', formData.email);
    const isPasswordValid = validateField('password', formData.password);
    const isConfirmPasswordValid = validateField('confirmPassword', formData.confirmPassword);

    if (!isUsernameValid || !isEmailValid || !isPasswordValid || !isConfirmPasswordValid) {
        notify.error("Please correct the errors before submitting.");
        return;
    }

    setIsSubmitting(true);
    try {
      const payload = { ...formData };
      if (isEditMode && !payload.password) {
        delete payload.password;
      }
      delete payload.confirmPassword;
      if (isEditMode) {
        await updateAdminUser(userId, payload);
      } else {
        await createAdminUser(payload);
      }
      notify.success(`User '${formData.username}' ${isEditMode ? 'updated' : 'created'} successfully!`);
      navigate('/admin/dashboard/users');
    } catch (err) {
      notify.error(err.response?.data?.message || err.message || `Failed to ${isEditMode ? 'update' : 'create'} user.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingData) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py="xl">
        <Spinner size="lg" />
        <Typography mt="md" color="secondary">Loading user data...</Typography>
      </Box>
    );
  }

  return (
    <Card title={formTitle}>
      {/* Remove the single `error` Alert, as errors are now per-field */}
      <form onSubmit={handleSubmit}>
        <Stack spacing="lg" pt="md">
          {/* Add onBlur and error props to each Input component */}
          <Input
            label="Username" id="username" name="username" value={formData.username}
            onChange={handleChange} onBlur={handleBlur}
            required disabled={isSubmitting || isEditMode}
            autoComplete="username"
            hintText={isEditMode ? "Username cannot be changed." : "Must be at least 3 characters."}
            error={formErrors.username}
          />
          <Input
            label="Email" id="email" name="email" type="email" value={formData.email}
            onChange={handleChange} onBlur={handleBlur}
            required disabled={isSubmitting} autoComplete="email"
            error={formErrors.email}
          />
          <Input
            label={isEditMode ? 'Set New Password (optional)' : 'Password'} id="password" name="password" type="password" value={formData.password}
            onChange={handleChange} onBlur={handleBlur}
            required={!isEditMode} disabled={isSubmitting} autoComplete="new-password"
            hintText={isEditMode ? "Leave blank to keep current password. Must be at least 8 characters." : "Must be at least 8 characters."}
            error={formErrors.password}
          />
          <Input
            label="Confirm Password" id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword}
            onChange={handleChange} onBlur={handleBlur}
            required={!isEditMode || !!formData.password} disabled={isSubmitting} autoComplete="new-password"
            error={formErrors.confirmPassword}
          />
          <Input
            label="First Name" id="first_name" name="first_name" value={formData.first_name}
            onChange={handleChange} disabled={isSubmitting}
          />
          <Input
            label="Last Name" id="last_name" name="last_name" value={formData.last_name}
            onChange={handleChange} disabled={isSubmitting}
          />
          <Input
            label="Organization" id="organization" name="organization" value={formData.organization}
            onChange={handleChange} disabled={isSubmitting}
          />
          <Box className={adminPageStyles.adminFormSection}>
            <Typography variant="body1" fontWeight="medium" gutterBottom>
              Assign Roles:
            </Typography>
            <Stack spacing="sm" mt="sm">
              {allRoles.length === 0 && !isLoadingData && (
                <Typography variant="caption" color="placeholder">No roles available to assign.</Typography>
              )}
              {allRoles.map(role => (
                <Checkbox
                  key={role.id} id={`role-${role.id}`} name="roles"
                  label={`${role.display_name || role.name} (${role.description || 'No description'})`}
                  value={role.name}
                  checked={formData.roles.includes(role.name)}
                  onChange={handleRoleChange}
                  disabled={isSubmitting || (role.name === 'system_admin' && isEditMode && formData.username === 'admin')}
                />
              ))}
            </Stack>
          </Box>
          {isEditMode && (
            <Checkbox
              label="User is Active" id="is_active" name="is_active"
              checked={formData.is_active} onChange={handleChange}
              disabled={isSubmitting || (formData.username === 'admin')}
            />
          )}
          <Stack
            direction="row"
            spacing="md"
            pt="lg"
            className={adminPageStyles.formActionRow} // Using shared class for border-top
            justifyContent="flex-start"
          >
            <Button type="submit" variant="primary" isLoading={isSubmitting} disabled={isSubmitting}>
              {isEditMode ? 'Save Changes' : 'Create User'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/admin/dashboard/users')} disabled={isSubmitting}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </form>
    </Card>
  );
}
export default UserFormPage;